import React, { useEffect, useState } from "react";
import "./App.css";
import { ConfigProvider, message, App as AntdApp, notification, theme, ConfigProviderProps } from "antd";
import { MainLayout } from "./layout/MainLayout";
import { BrowserRouter } from "react-router-dom";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloak from "./keycloak";
import hr_HR from 'antd/locale/hr_HR';
import en_US from 'antd/locale/en_US';

import dayjs from 'dayjs';
import 'dayjs/locale/hr';
import "./i18n";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { AxiosProvider } from "./context/AxiosContext";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useExamStore, ThemeMode } from "@store";
import { useTranslation } from "react-i18next";
import { useThemeDetector } from "@hooks";

type Locale = ConfigProviderProps['locale'];


function App() {
  const appTheme = useExamStore((state) => state.theme);
  const setTheme = useExamStore((state) => state.setTheme);
  const [messageApi, contextHolder] = message.useMessage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, notifHolder] = notification.useNotification();
  const {isDarkTheme} = useThemeDetector();
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState<Locale>(hr_HR || undefined)

  const [queryClient] = useState(() => 
    new QueryClient({
      queryCache: new QueryCache({
        onError: (error) => {
          messageApi.error(error.message);
        },
      }),
      mutationCache: new MutationCache({
        onError: (error) => {
          messageApi.error(error.message);
        },
      }),
    })
  );

  const returnAlgorithm = () => {
    const root = document.documentElement;
    if (appTheme === "dark") {
      root.classList.add('dark');
      return theme.darkAlgorithm;
    } else if (appTheme === "light") {
      root.classList.remove('dark');
      return theme.defaultAlgorithm;
    } else {
      if (isDarkTheme) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      return isDarkTheme ? theme.darkAlgorithm : theme.defaultAlgorithm;
    }
  
  }

  const returnColor = (header?: boolean) => {
    if (appTheme === "dark") {
      return header ? "#141414" : "#1f1f1f";
    } else if (appTheme === "light") {
      return "white";
    } else {
      return isDarkTheme ? header ? "#141414" : "#1f1f1f" : "white";
    }
  
  }

  useEffect(() => {
    if (localStorage.getItem("theme") === null) {
      setTheme(ThemeMode.Light, isDarkTheme);
    } else {
      setTheme(localStorage.getItem("theme") as ThemeMode, isDarkTheme);
    }
    
  }, [isDarkTheme, setTheme]);

  useEffect(() => {
    dayjs.locale(i18n.language);
    setLocale(i18n.language === "hr" ? hr_HR : en_US)
  }, [i18n.language]);

  return (
    <ReactKeycloakProvider
          authClient={keycloak}
          initOptions={{
            checkLoginIframe: true,
            onLoad: "login-required",
          }}
        >
    <ConfigProvider
      locale={locale}
      theme={{
        cssVar: true,
        algorithm: returnAlgorithm(),
        token: {
          colorPrimary: "#F1B80E",
          fontFamily: "Nunito",
        },
        components: {
          Layout: {
            bodyBg: returnColor(),
            headerBg: returnColor(true),
            siderBg: returnColor()
          },
          
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        {contextHolder}
        {notifHolder}
        
          <React.StrictMode>
            <AntdApp>
              <BrowserRouter>
                <AxiosProvider>
                  <MainLayout />
                </AxiosProvider>
              </BrowserRouter>
            </AntdApp>
            <ReactQueryDevtools initialIsOpen={false} />
          </React.StrictMode>
       

      </QueryClientProvider>
    </ConfigProvider>
    </ReactKeycloakProvider>

    
  );
}

export default App;
